import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "../hooks/useTranslation";
import { useWallet } from "../hooks/useWallet";
import { API_BASE } from "../constants/index";

const DEPOSIT_HISTORY_API = `${API_BASE}/publicApi/getDepositHistory`;

// Map mg_bc_deposit row -> UI record (bcd_* fields from API)
function mapDepositRecord(row) {
    const amountRaw = row.bcd_amount ?? row.bd_amount ?? row.amount ?? 0;
    const amountNum = Number(amountRaw);
    const timestamp =
        row.bcd_cdate ??
        row.bd_cdate ??
        row.created_at ??
        row.datetime ??
        row.timestamp ??
        null;
    const status = row.bcd_status ?? row.bd_status ?? row.status ?? "success";
    const currency = row.currency ?? "USDT";

    return {
        id: row.bcd_id ?? row.bd_id ?? row.id,
        amount: Number.isNaN(amountNum) ? amountRaw : amountNum,
        timestamp,
        status,
        currency,
        raw: row,
    };
}

export default function DepositHistory() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { account } = useWallet();
    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [timeRange, setTimeRange] = useState("All"); // All | Today | 1W | 1M | 3M
    const [currentPage, setCurrentPage] = useState(0);
    const [showFilter, setShowFilter] = useState(false);

    const pageSize = 10;

    const getTimeRangeBoundary = () => {
        if (timeRange === "All") return 0;
        const now = new Date();
        const startOfToday = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
        ).getTime();
        switch (timeRange) {
            case "Today":
                return startOfToday;
            case "1W":
                return startOfToday - 7 * 86400000;
            case "1M":
                return startOfToday - 30 * 86400000;
            case "3M":
                return startOfToday - 90 * 86400000;
            default:
                return 0;
        }
    };

    const handleResetFilter = () => {
        setTimeRange("All");
    };

    const handleDoneFilter = () => {
        setShowFilter(false);
        setCurrentPage(0);
    };

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!account) {
                setRecords([]);
                return;
            }

            setIsLoading(true);
            setApiError(null);

            try {
                const res = await fetch(DEPOSIT_HISTORY_API, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ userAddress: account }),
                });
                const data = await res.json().catch(() => ({}));
                console.log("deposit history data:", data);
                if (cancelled) return;

                if (data.success && Array.isArray(data.records)) {
                    const mapped = data.records.map(mapDepositRecord);
                    mapped.sort((a, b) => {
                        if (!a.timestamp || !b.timestamp) return 0;
                        const ta = new Date(a.timestamp).getTime();
                        const tb = new Date(b.timestamp).getTime();
                        if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
                        return tb - ta;
                    });
                    setRecords(mapped);
                } else if (data.success === false) {
                    setApiError(data.error || "Failed to load deposit history");
                    setRecords([]);
                } else {
                    setRecords([]);
                }
            } catch (err) {
                if (!cancelled) {
                    setApiError(err.message || "Network error");
                    setRecords([]);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [account]);

    // 时间过滤 + 分页
    const filteredRecords = records.filter((r) => {
        const minTime = getTimeRangeBoundary();
        if (!minTime) return true;
        if (!r.timestamp) return false;
        const tMs = new Date(r.timestamp).getTime();
        if (Number.isNaN(tMs)) return false;
        return tMs >= minTime;
    });

    const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
    const safePage = Math.min(currentPage, totalPages - 1);
    const pageRecords = filteredRecords.slice(
        safePage * pageSize,
        safePage * pageSize + pageSize
    );

    return (
        <div className="relative overflow-x-hidden -mx-4 -my-8 min-h-[calc(100vh-200px)]">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0610] via-[#241630] to-[#0a0610] -z-10" />
            <div
                className="absolute inset-0 opacity-[0.03] pointer-events-none -z-10"
                style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, #9770DD 1px, transparent 0)`,
                    backgroundSize: "40px 40px",
                }}
            />
            <div
                className="absolute inset-0 opacity-[0.02] pointer-events-none -z-10"
                style={{
                    backgroundImage: `linear-gradient(45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%), linear-gradient(-45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%)`,
                    backgroundSize: "60px 60px",
                }}
            />

            <div className="relative z-10 pt-6 pb-6 px-4 max-w-md mx-auto space-y-6">
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center size-10 rounded-full text-white hover:bg-white/10 transition-colors"
                        aria-label={t("common.goBack") || "Go back"}
                    >
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </button>
                    <h1 className="text-2xl font-semibold text-white">
                        {t("depositHistory.title") || "Deposit History"}
                    </h1>
                </div>

                <div className="rounded-3xl bg-[#1a1023]/70 backdrop-blur-md border border-[#9770DD]/30 p-5 shadow-lg shadow-[#9770DD]/15">
                    <div className="flex items-center justify-between gap-3 mb-5">
                        <div className="flex items-center gap-3">
                            <span
                                className="material-symbols-outlined text-gray-300 text-2xl"
                                style={{ filter: "drop-shadow(0 2px 4px rgba(255,255,255,0.3))" }}
                            >
                                history
                            </span>
                            <h2 className="text-lg font-semibold text-white">
                                {t("depositHistory.records") || "Deposit Records"}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowFilter(true)}
                            className="size-10 rounded-lg bg-[#241630]/60 border border-[#9770DD]/20 hover:bg-[#2d1b3d]/60 transition-colors flex items-center justify-center"
                            aria-label={t("common.filterLabel")}
                        >
                            <span className="material-symbols-outlined text-[#9770DD] text-lg">
                                filter_list
                            </span>
                        </button>
                    </div>

                    {apiError && (
                        <div className="mb-4 py-2 px-3 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-200 text-sm">
                            {apiError}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="py-12 text-center text-[#ad90cb]">
                            <span className="material-symbols-outlined animate-spin text-3xl">progress_activity</span>
                            <p className="mt-2">{t("common.loading") || "Loading..."}</p>
                        </div>
                    ) : filteredRecords.length === 0 ? (
                        <div className="py-12 text-center text-[#ad90cb]">
                            <span className="material-symbols-outlined text-4xl text-[#9770DD]/50">account_balance_wallet</span>
                            <p className="mt-3 text-sm">{t("depositHistory.noRecords") || "No deposit records yet"}</p>
                            <p className="mt-1 text-xs text-white/50">
                                {t("depositHistory.noRecordsHint") || "Your deposit history will appear here."}
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate("/deposit")}
                                className="mt-6 px-6 py-2.5 rounded-xl bg-[#9770DD] text-white text-sm font-semibold hover:bg-[#7a1fd6] transition-colors"
                            >
                                {t("depositHistory.goToDeposit") || "Go to Deposit"}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {pageRecords.map((record, index) => {
                                const amountNum = Number(record.amount);
                                const displayAmount = Number.isNaN(amountNum)
                                    ? record.amount
                                    : amountNum.toLocaleString(undefined, {
                                          maximumFractionDigits: 4,
                                      });
                                const dateStr = record.timestamp
                                    ? String(record.timestamp)
                                    : record.raw?.bcd_cdate ??
                                      record.raw?.bd_cdate ??
                                      record.raw?.created_at ??
                                      "-";
                                const statusText = record.status || "Completed";

                                return (
                                    <div
                                        key={record.id || index}
                                        className="flex items-center justify-between py-3 px-4 rounded-xl bg-[#241630]/50 border border-[#9770DD]/20"
                                    >
                                        <div>
                                            <p className="text-white font-medium">
                                                {displayAmount} {record.currency || "USDT"}
                                            </p>
                                            <p className="text-[#ad90cb] text-xs">{dateStr}</p>
                                        </div>
                                        <span className="text-xs px-2 py-1 rounded-full bg-[#9770DD]/20 text-[#DFD2FE]">
                                            {statusText}
                                        </span>
                                    </div>
                                );
                            })}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between pt-3 mt-2 border-t border-[#9770DD]/20 text-xs text-[#ad90cb]">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setCurrentPage((p) => Math.max(p - 1, 0))
                                        }
                                        disabled={safePage === 0}
                                        className={`px-3 py-1 rounded-lg border border-[#9770DD]/40 hover:border-[#DFD2FE] transition-colors ${
                                            safePage === 0
                                                ? "opacity-40 cursor-not-allowed"
                                                : ""
                                        }`}
                                    >
                                        {t("history.prev") || t("common.prev") || "Prev"}
                                    </button>
                                    <span>
                                        {(t("history.page") || "Page")} {safePage + 1}{" "}
                                        {t("history.of") || "of"} {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setCurrentPage((p) =>
                                                Math.min(p + 1, totalPages - 1)
                                            )
                                        }
                                        disabled={safePage >= totalPages - 1}
                                        className={`px-3 py-1 rounded-lg border border-[#9770DD]/40 hover:border-[#DFD2FE] transition-colors ${
                                            safePage >= totalPages - 1
                                                ? "opacity-40 cursor-not-allowed"
                                                : ""
                                        }`}
                                    >
                                        {t("history.next") || t("common.next") || "Next"}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Filter Bottom Sheet Modal - same UX as History */}
            <AnimatePresence>
                {showFilter && (
                    <div className="fixed inset-0 z-[100] pointer-events-none">
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowFilter(false)}
                            className="absolute inset-0 bg-gray-950/50 pointer-events-auto"
                        />

                        {/* Bottom Sheet */}
                        <div className="absolute inset-0 flex items-end justify-center pointer-events-none">
                            <motion.div
                                initial={{ y: "100%" }}
                                animate={{ y: 0 }}
                                exit={{ y: "100%" }}
                                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                className="w-full max-w-md bg-[#0a0610] rounded-t-3xl border-t border-[#9770DD]/30 shadow-2xl pointer-events-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Drag Handle */}
                                <div className="flex justify-center pt-3 pb-2">
                                    <div className="w-12 h-1 bg-gray-600 rounded-full"></div>
                                </div>

                                {/* Filter Content */}
                                <div className="px-5 pb-6 pt-2">
                                    {/* Title */}
                                    <h2 className="text-2xl font-semibold text-white mb-6">
                                        {t("history.filter") ||
                                            t("common.filter") ||
                                            "Filter"}
                                    </h2>

                                    {/* Time Range Section */}
                                    <div className="mb-6">
                                        <h3 className="text-white text-base font-medium mb-4">
                                            {t("history.timeRange") || "Time Range"}
                                        </h3>

                                        {/* Time Range Buttons */}
                                        <div className="grid grid-cols-3 gap-3 mb-4">
                                            {["All", "Today", "1W", "1M", "3M"].map((range) => (
                                                <button
                                                    key={range}
                                                    type="button"
                                                    onClick={() => setTimeRange(range)}
                                                    className={`h-12 rounded-xl font-semibold text-sm transition-all ${
                                                        timeRange === range
                                                            ? "bg-[#9770DD] text-white shadow-lg shadow-[#9770DD]/30"
                                                            : "bg-[#241630]/60 text-white border border-[#9770DD]/20 hover:border-[#9770DD]/40"
                                                    }`}
                                                >
                                                    {t(
                                                        `history.time${
                                                            range === "All"
                                                                ? "All"
                                                                : range === "Today"
                                                                ? "Today"
                                                                : range === "1W"
                                                                ? "1W"
                                                                : range === "1M"
                                                                ? "1M"
                                                                : "3M"
                                                        }`
                                                    ) || range}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex gap-3 mt-8 mb-4">
                                        <button
                                            type="button"
                                            onClick={handleResetFilter}
                                            className="flex-1 h-14 rounded-2xl bg-[#241630]/60 border border-[#9770DD]/20 text-white font-bold text-lg hover:bg-[#2d1b3d]/60 hover:border-[#9770DD]/40 transition-all"
                                        >
                                            {t("history.reset") ||
                                                t("common.reset") ||
                                                "Reset"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleDoneFilter}
                                            className="flex-1 h-14 rounded-2xl bg-[#9770DD] text-white font-bold text-lg hover:bg-[#7a1fd6] transition-all shadow-lg shadow-[#9770DD]/30"
                                        >
                                            {t("history.done") ||
                                                t("common.done") ||
                                                "Done"}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
